import { useCallback, useEffect, useState } from 'react';
import { identityApi } from '@/api/identity.api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { PORTAL_BRANDING } from '@/config/portalConfig';
import type { PortalDashboard, PortalCode } from '@/types/identity';
import { showApiError } from '@/utils/apiError';

const KPI_LABELS: Record<string, string> = {
  openOrders: 'Open orders',
  unpaidInvoices: 'Unpaid invoices',
  openTickets: 'Open tickets',
  pendingPos: 'Pending POs',
  openRfqs: 'Open RFQs',
  pendingPayments: 'Pending payments',
  leaveBalance: 'Leave balance',
  pendingApprovals: 'Pending approvals',
  upcomingTraining: 'Upcoming training',
  activeApplications: 'Active applications',
  upcomingInterviews: 'Upcoming interviews',
  onboardingTasks: 'Onboarding tasks',
  assignedDeliveries: 'Assigned deliveries',
  completedToday: 'Completed today',
  pendingPod: 'Pending POD',
  activeDeals: 'Active deals',
  pendingDocuments: 'Pending documents',
};

export function IdentityPortalDashboardPage() {
  const session = useAppSelector((s) => s.identity.session);
  const [dashboard, setDashboard] = useState<PortalDashboard | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await identityApi.dashboard();
      setDashboard(data);
    } catch (err) {
      showApiError(err, 'Could not load dashboard');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (!session || !dashboard) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  const branding = PORTAL_BRANDING[session.activePortalCode as PortalCode];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {branding.title}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Welcome back, {dashboard.displayName}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(dashboard.kpis).map(([key, value]) => (
          <div key={key} className="card p-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {KPI_LABELS[key] ?? key}
            </p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-1">
              {value}
            </p>
          </div>
        ))}
      </div>

      {dashboard.workQueue.length > 0 && (
        <section className="card p-5">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100">Work queue</h2>
          <ul className="mt-4 divide-y divide-slate-100 dark:divide-slate-800">
            {dashboard.workQueue.map((item) => (
              <li key={item.id} className="py-3 flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{item.title}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{item.category}</p>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  item.priority === 'HIGH'
                    ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300'
                    : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                }`}>
                  {item.priority}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
